// src/hooks/useApi.js

import { useState, useCallback } from 'react';

// Lee la URL base de la API desde las variables de entorno una sola vez
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Comprobación inicial crucial: Si no hay URL base, la aplicación no puede funcionar.
if (!API_BASE_URL) {
  console.error("FATAL ERROR: REACT_APP_API_URL is not defined in environment variables. API calls will fail.");
  // Podrías lanzar un error aquí para detener la carga de la app si lo prefieres:
  // throw new Error("REACT_APP_API_URL environment variable is not set.");
}

/**
 * Custom Hook para realizar llamadas a la API backend.
 * Maneja la URL base, token de autorización, estados de carga y error.
 *
 * @returns {object} Un objeto con:
 *   - data: Los datos recibidos de la API en caso de éxito (null si no hay datos o error).
 *   - loading: Un booleano indicando si la petición está en curso.
 *   - error: Un objeto de error si la petición falló (null si fue exitosa).
 *   - request: Una función asíncrona para ejecutar la petición API (¡ESTA FUNCIÓN ES ESTABLE!).
 */
const useApi = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Función para ejecutar la petición a la API.
   * @param {string} endpoint - El endpoint específico de la API (ej. '/api/login', '/api/me').
   * @param {object} options - Opciones para la función fetch (method, body, headers adicionales, etc.).
   *                           El 'Content-Type': 'application/json' y 'Authorization' se añaden automáticamente.
   * @returns {Promise<any>} Una promesa que resuelve con los datos de la respuesta en caso de éxito.
   * @throws {Error} Lanza un error si la petición falla (el error también se guarda en el estado 'error').
   */
  const request = useCallback(async (endpoint, options = {}) => {
    // Si no hay URL base configurada, no podemos continuar.
    if (!API_BASE_URL) {
      const err = new Error("Configuración de API ausente (REACT_APP_API_URL).");
      // Actualizar el estado de error aquí es seguro porque request no depende de él.
      setError(err);
      console.error(err.message);
      throw err; // Detener la ejecución
    }

    setLoading(true);
    setError(null); // Limpiar errores previos al iniciar una nueva petición
    setData(null); // Limpiar datos previos

    const url = `${API_BASE_URL}${endpoint}`; // Construir URL completa
    console.log(`DEBUG [useApi]: Making ${options.method || 'GET'} request to ${url}`);

    const token = localStorage.getItem('access_token'); // Recuperar token

    // Preparar cabeceras
    const headers = {
      ...(options.body && typeof options.body === 'string'
         ? { 'Content-Type': 'application/json' }
         : {}),
      ...options.headers,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers: headers,
      });

      let responseBody = null;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
          responseBody = await response.json().catch(() => null);
      } else if (response.status !== 204) {
          console.log(`DEBUG [useApi]: Response ${response.status}, Content-Type: ${contentType}`);
      }

      if (!response.ok) {
        const errorMessage = responseBody?.error || responseBody?.message || `Error HTTP ${response.status}: ${response.statusText}`;
        const fetchError = new Error(errorMessage);
        fetchError.status = response.status;
        fetchError.data = responseBody;
        console.error(`ERROR [useApi] (${response.status}):`, fetchError.message, fetchError.data || '');
        setError(fetchError); // Actualiza el estado de error
        throw fetchError; // Lanza el error
      }

      // Éxito
      console.log(`DEBUG [useApi]: Request successful (${response.status})`, responseBody);
      setData(responseBody); // Actualiza el estado de datos
      return responseBody; // Devuelve los datos

    } catch (err) {
      // Captura errores de red o los lanzados por !response.ok
      // Actualiza el estado de error solo si no se hizo ya (en la práctica, siempre se hará aquí si hubo error)
      setError(prevError => prevError || err);
      console.error("ERROR [useApi] (Network/Catch):", err);
      throw err; // Re-lanzar para el componente
    } finally {
      setLoading(false); // Quitar estado de carga
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // <<<--- ¡¡¡ ARRAY DE DEPENDENCIAS VACÍO !!!
           // Esto asegura que la función 'request' SÓLO se crea una vez por instancia del hook.

  // Devolver el estado y la función request (que ahora es estable)
  return { data, loading, error, request };
};

export default useApi;